import React, { useEffect, useState } from 'react'
import { all_routes } from '../../router/all_routes'
import { Link, useNavigate } from 'react-router-dom'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import InstructorSidebar from '../common/instructorSidebar'
import { useForm, FieldPath } from 'react-hook-form'
import {
  UserEditFormValues,
  userEditSchema,
  userAddFields,
} from '../../../config/fields/user'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from '@tanstack/react-query'
import { postUser } from '../../../api/user'
import { Role_Option } from '../../../core/common/selectOption/json/selectOption'
import { Select } from 'antd'
import { useNotify } from '../../../provider/NotificationProvider'
import * as yup from 'yup'
import { useAuth } from '../../../provider/AuthProvider'

type BackendValidationError = {
  errors: Record<string, string[]>
}

// ✅ Extend the schema: password wajib
const userAddSchema = userEditSchema.shape({
  password: yup
    .string()
    .required('Password wajib diisi')
    .min(6, 'Password minimal 6 karakter'),
})

const defaultValues: UserEditFormValues = {
  fullname: '',
  email: '',
  phone_number: '',
  role: '',
  nik: '',
  address: '',
  profession: '',
  bio: '',
  profile_picture: null,
  password: '',
}

const UserAdd = () => {
  const { user } = useAuth();

  const filteredRoleOptions = user?.role === 'admin'
    ? Role_Option // admin sees all
    : Role_Option.filter((option) => option.value !== 'admin')

  const [preview, setPreview] = useState<string | null>(null)
  const notify = useNotify()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError, // ✅ add this
    formState: { errors },
  } = useForm<UserEditFormValues>({
    resolver: yupResolver(userAddSchema),
    mode: 'onBlur',
    defaultValues,
  })

  // ✅ Unified backend error handler
  const handleError = (err: any) => {
    const data = err.response?.data as BackendValidationError

    if (!data?.errors) return

    Object.entries(data.errors).forEach(([key, messages]) => {
      // ✅ Explicitly tell TS that this key matches a form field name
      const field = key as FieldPath<UserEditFormValues>

      setError(field, {
        type: 'manual',
        message: Array.isArray(messages) ? messages[0] : String(messages),
      })
    })
  }

  const createMutation = useMutation({
    mutationFn: (formData: FormData) => postUser(formData),

    onSuccess: () => {
      notify.success({
        message: 'User berhasil ditambahkan',
        description: 'Data user baru berhasil disimpan',
      })
      navigate(all_routes.adminUser)
    },

    onError: (err: any) => {
      const data = err?.response?.data

      // 🧩 1. Show global toast
      notify.error({
        message: 'Tambah user gagal',
        description: data?.message || 'Terjadi kesalahan.',
      })

      // 🧩 2. Map Laravel field errors to react-hook-form
      handleError(err)
    },
  })

  const onSubmit = (data: any) => {
    const formData = new FormData()

    for (const key in data) {
      if (key === 'profile_picture') {
        const file = data?.profile_picture?.[0]
        if (file) formData.append('profile_picture', file)
      } else if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key] as any)
      }
    }

    createMutation.mutate(formData)
  }

  const profilePicture: any = watch('profile_picture')

  useEffect(() => {
    if (profilePicture?.[0]?.name) {
      const objectUrl = URL.createObjectURL(profilePicture[0])
      setPreview(objectUrl)
      return () => URL.revokeObjectURL(objectUrl)
    }
  }, [profilePicture])

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <InstructorSidebar />

          <div className="col-lg-9">
            <Link to="/admin/user" className="d-flex align-items-center mb-3">
              <i className="isax isax-arrow-left me-1 fw-bold" />
              Back to List
            </Link>

            <div className="card">
              <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  {/* Profile Photo */}
                  <div className="profile-upload-group mb-4">
                    <div className="d-flex align-items-center">
                      <div className="avatar flex-shrink-0 avatar-xxxl avatar-rounded border me-3">
                        <ImageWithBasePath
                          src={preview || '/assets/img/user/user-02.jpg'}
                          alt="Img"
                          className="img-fluid"
                        />
                      </div>
                      <div className="profile-upload-head">
                        <h6>Profile Photo</h6>
                        <p className="fs-14 mb-0">PNG or JPG</p>
                        <div className="new-employee-field">
                          <div className="d-flex align-items-center mt-2">
                            <div className="image-upload position-relative mb-0 me-2">
                              <input type="file" {...register('profile_picture')} />
                              <Link
                                to="#"
                                className="btn bg-gray-100 btn-sm rounded-pill image-uploads"
                              >
                                Upload
                              </Link>
                            </div>
                          </div>
                        </div>
                        {errors.profile_picture && (
                          <p className="text-danger mt-1">
                            {errors.profile_picture.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Personal Details */}
                  <div className="edit-profile-info mb-3">
                    <h5 className="mb-1">Personal Details</h5>
                    <p>Isi data untuk membuat user baru</p>
                  </div>

                  <div className="row">
                    {userAddFields.map(([name, config]) => {
                      if (name === 'profile_picture') return null

                      if (name === 'role') {
                        return (
                          <div key={name} className="col-md-6 mb-3">
                            <label className="form-label">
                              {config.label}{' '}
                              {config.required && <span className="text-danger">*</span>}
                            </label>
                            <Select
                              placeholder={config.placeholder}
                              style={{ width: '100%' }}
                              options={filteredRoleOptions}
                              onChange={(value) => setValue(name as FieldPath<UserEditFormValues>, value)}
                            />
                            {errors[name] && (
                              <p className="text-danger mt-1">
                                {errors[name]?.message as string}
                              </p>
                            )}
                          </div>
                        )
                      }

                      if (config.type === 'textarea') {
                        return (
                          <div key={name} className="col-md-12 mb-3">
                            <label className="form-label">
                              {config.label}{' '}
                              {config.required && <span className="text-danger">*</span>}
                            </label>
                            <textarea
                              rows={4}
                              {...register(name as FieldPath<UserEditFormValues>)}
                              className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
                              placeholder={config.placeholder}
                            />
                            {errors[name] && (
                              <p className="text-danger mt-1">
                                {errors[name]?.message as string}
                              </p>
                            )}
                          </div>
                        )
                      }

                      return (
                        <div key={name} className="col-md-6 mb-3">
                          <label className="form-label">
                            {config.label}{' '}
                            {config.required && <span className="text-danger">*</span>}
                          </label>
                          <input
                            type={config.type}
                            {...register(name as FieldPath<UserEditFormValues>)}
                            className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
                            placeholder={config.placeholder}
                          />
                          {errors[name] && (
                            <p className="text-danger mt-1">
                              {errors[name]?.message as string}
                            </p>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  <div className="col-md-12">
                    <button
                      disabled={createMutation.isPending}
                      type="submit"
                      className="btn btn-secondary rounded-pill"
                    >
                      Tambah User
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserAdd
